import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { updateWatchedFieldsUtils } from "src/app/features/Site/SiteAndSiteGroupUtils";
import { useSiteGroupLoadQuery } from "src/app/features/SiteGroup/services/server/SiteGroupApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useSiteLoadQuery, useSiteUpdateMutation } from "../../services/server/SiteApi";
import type { SiteEntity } from "../../types/SiteEntity";

export const SiteEditForm: FC<FormProps> = ({ runSubmit }) => {
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useSiteUpdateMutation();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const [formData, setFormData] = useState<SiteEntity>({} as SiteEntity);
    const prevParentUnitIdRef = useRef<ID | undefined>(undefined);
    const formDataRef = useRef(formData);

    // HANDLE INITIAL VALUES
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("site");
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useSiteLoadQuery({ entityId: id });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
            formDataRef.current = loadFormData;
            prevParentUnitIdRef.current = loadFormData.parentUnit;
        }
    }, [loadFormData, setFormData]);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback((newData: SiteEntity) => {
        const updatedData = { ...newData, statusCheckReadOnly: formDataRef.current?.statusCheckReadOnly };
        setFormData(updatedData);
        formDataRef.current = updatedData;
    }, []);

    // HANDLE WATCH FIELDS
    const { data: siteGroupEntity } = useSiteGroupLoadQuery(
        { entityId: formData?.parentUnit ? `${formData.parentUnit}` : undefined },
        { skip: !formData?.parentUnit },
    );

    const updateWatchedFields = useCallback(() => {
        const currentParentUnitId = formDataRef.current?.parentUnit;

        if (!currentParentUnitId || !siteGroupEntity || !prevParentUnitIdRef.current) return null;

        prevParentUnitIdRef.current = currentParentUnitId;
        if (!siteGroupEntity || !formDataRef.current) return null;

        return updateWatchedFieldsUtils(siteGroupEntity, formDataRef.current);
    }, [siteGroupEntity]);

    useEffect(() => {
        const currentParentUnitId = formData?.parentUnit;

        if (!siteGroupEntity || !prevParentUnitIdRef.current || !currentParentUnitId) {
            return;
        }

        const prevParentUnitId = prevParentUnitIdRef.current;

        if (currentParentUnitId !== prevParentUnitId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [formData, updateWatchedFields, siteGroupEntity]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Site",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.SITES}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<SiteEntity>
                    name="SiteEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
